/* SALIDAS BITREN y Enganches, de los dos lados.
 *
 * Lautaro Díaz (Satelital), 21/10/2026, hilo «SALIDAS DE BUE EN ONLY
 * BITRENES Y SALIDAS Y CORTES»:
 *
 *   «cuando se cargue en salidas de only bitrenes lo que hay en cargas
 *   diarias, tambien se cargue en enganches, para que yo solo tenga que
 *   cargar el chofer que saca la 2da parte hasta rgd y listo. Y tambien una
 *   vez que yo envie el correo del enganche, en vez de unir en la planilla
 *   (como los que salen de rgd) que se separe y pongas al chofer que lleve
 *   la 2da parte»
 *
 * ── 1. AL AGREGAR A SALIDAS BITREN, A ENGANCHES ─────────────────────────────
 *
 * Los bitrenes que «Agregar … a SALIDAS BITREN» acaba de escribir, y los que
 * ese día ya estaban en la planilla (escritos a mano, o agregados antes de
 * esto), van a FlotaBot (`/api/satelital/salidas-bitren/enganches`), que los carga en
 * Enganches hacia Río Grande con la 1ra parte (bitren, tractor, chofer,
 * cliente, el día de salida) y el furgón de la 2da; el tractor y el chofer
 * de la 2da quedan vacíos para que él los complete. Allá se decide qué ya
 * está (no duplica nada equivalente); acá sólo se arma lo que se manda
 * (`bitrenesParaEnganches`). Si ya estaban todos, «Pasar … a Enganches» los
 * manda sin tocar la planilla. Lo que Enganches no acepta vuelve con su
 * motivo y se nombra (`textoEnganches`).
 *
 * ── 2. AL GENERAR EL CORREO DEL ENGANCHE, SE SEPARA ─────────────────────────
 *
 * En su SALIDAS BITREN cada bitren son dos renglones, todo combinado de a
 * dos menos FUR: arriba el par con el titular, abajo el impar solo. Cuando
 * genera en Enganches el correo de uno hacia Río Grande (`sentido` «sur»),
 * el bitren se separa: se sacan las combinadas de esos dos renglones y en el
 * de abajo van el F/C y el F/S de arriba, el TRAC y el CHOFER de la 2da
 * parte, el mismo cliente y, en UBICACIÓN, GALLEGOS (donde la 2da parte
 * espera a quien la cruza). Su ejemplo:
 *
 *   F/C | F/S | BITREN | TRA  | CHOFER        | CLIENTE        | UBICACIÓN
 *   20  | 20  | 1502   | 1013 | BRIOZZI JORGE | SOLNICK WILSON | LOGINTER -PUERTO
 *   20  | 20  | 1503   | 745  | SEGOVIA MIRKO | SOLNICK WILSON | GALLEGOS
 *
 * Ese GALLEGOS no lo pisa la UBICACIÓN de las 08:00 de FlotaBot
 * (`ubicacion_manana`) mientras el enganche no se hizo: el renglón ya tiene
 * su propio TRA, y sin eso a la mañana siguiente quedaba la posición del
 * tractor de la 2da parte, que todavía no lo pasó a buscar.
 *
 * Es lo contrario de ARRIBOS BITREN (`shared/arribosUnificar`), que con el
 * correo de uno hacia Buenos Aires se unifica. Lo dispara lo mismo: FlotaBot
 * avisa por mensaje al armazón (`PanelesFlotaBot`).
 *
 * QUÉ RENGLONES. Sólo los de ese bitren: FUR con el 1er semi y, pegado
 * abajo, el 2do (el FURGÓN 2DA PARTE del enganche: si no está, no se deduce
 * y se dice). Del día de salida del enganche (el F/C de arriba, que el de
 * abajo arrastra); con el par dos veces ese día, el último. Si no está, no
 * se toca nada y se dice.
 *
 * NADA SE PISA. Lo de abajo se escribe sólo en celdas vacías; si ya tiene
 * otra cosa queda como está y se nombra. Sólo se sacan las combinadas de
 * una columna y justo esos dos renglones (las que se ponen al agregar); si
 * una suya se cruza con esos renglones de otra forma, no se separa nada y
 * se dice: separar a medias sería peor. El chofer de la 2da va como él lo
 * escribe en sus hojas si es el mismo apellido (`choferLargo`), y un
 * fletero (una patente en el enganche) como FLT, como en sus hojas.
 *
 * Todo es puro: la hoja que entra no se toca.
 */

import { refDe } from "./planilla";
import { normalizarCombinadas, rangoCombinada, textoCombinada } from "./planillaCombinadas";
import { encabezadoUbicacionFija } from "./pernocteBitren";
import { encabezadoSalidas, choferLargo, clienteCorto } from "./salidasBitren";
import { pelado } from "./torrecillas";

export const HOJA_SALIDAS = "SALIDAS BITREN";
export const EN_GALLEGOS = "GALLEGOS";

const txt = (v) => String(v ?? "").replace(/\s+/g, " ").trim();
const igual = (a, b) => txt(a).toUpperCase() === txt(b).toUpperCase();

/* ── 1. A Enganches ───────────────────────────────────────────────────────── */

/* Los bitrenes agregados (`bitrenes` de `agregarASalidas`) → lo que va a
   FlotaBot: `[{fecha, bitren, furgon2, tractor, chofer, cliente}]`. Sólo los
   que tienen el 1er semi como interno. */
export function bitrenesParaEnganches(bitrenes, fecha) {
  return (bitrenes || [])
    .filter((b) => /^\d{1,5}$/.test(txt(b.primera)))
    .map((b) => ({
      fecha,
      bitren: txt(b.primera),
      furgon2: /^\d{1,5}$/.test(txt(b.segunda)) ? txt(b.segunda) : "",
      tractor: txt(b.tractor),
      chofer: txt(b.chofer),
      cliente: txt(b.cliente),
    }));
}

/* «A, B y C». */
const enumerar = (xs) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`);

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/* La línea de la pantalla después de mandarlos (`r` es lo que contesta
   FlotaBot, o `{ok: false, mensaje}`). `salteadas` (`[{bitren, motivo}]`)
   son los que Enganches no aceptó: se nombran con su motivo. */
export function textoEnganches(r) {
  if (!r?.ok) {
    return `No pude cargarlos en Enganches (${txt(r?.mensaje || r?.error) || "no contestó"}): `
      + "en SALIDAS BITREN quedaron igual.";
  }
  const partes = [];
  if (r.nuevas) partes.push(`${plural(r.nuevas, "enganche cargado", "enganches cargados")}, con la 2da parte para completar`);
  if (r.ya_estaban) partes.push(`${r.ya_estaban} ya ${r.ya_estaban === 1 ? "estaba" : "estaban"}`);
  if (r.fallaron) partes.push(`${plural(r.fallaron, "no se pudo cargar", "no se pudieron cargar")}`);
  const salteadas = (r.salteadas || []).filter((s) => txt(s?.bitren));
  if (salteadas.length) {
    partes.push(`no ${salteadas.length === 1 ? "entró" : "entraron"} `
      + enumerar(salteadas.map((s) => `el ${txt(s.bitren)}${txt(s.motivo) ? ` (${txt(s.motivo)})` : ""}`)));
  }
  return partes.length ? `Enganches: ${partes.join(", ")}.` : "";
}

/* ── 2. Separar ───────────────────────────────────────────────────────────── */

const fechaISO = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(txt(v));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

/* Lo que manda FlotaBot al generar el correo → lo que hace falta para
   separar, o null. `trae2da` es si el FlotaBot que lo mandó ya manda la 2da
   parte: uno de antes no la manda, y eso no es «no está cargada». */
export function datosParaSeparar(m) {
  if (!m || typeof m !== "object") return null;
  return {
    id: m.id ?? null,
    sentido: txt(m.sentido).toLowerCase(),
    bitren: txt(m.bitren),
    furgon2: txt(m.furgon2),
    tractor2: txt(m.tractor2),
    chofer2: txt(m.chofer2),
    cliente: txt(m.cliente),
    fecha: fechaISO(m.fecha_salida),
    trae2da: "tractor2" in m || "chofer2" in m,
  };
}

const par = (e) => `${e.bitren}/${e.furgon2}`;

/* `{separar: true}`, o `{separar: false, texto}` (vacío: no es el caso). */
export function correspondeSeparar(e) {
  if (!e || e.sentido !== "sur" || !pelado(e.bitren) || !e.trae2da) return { separar: false, texto: "" };
  if (!pelado(e.furgon2)) {
    return {
      separar: false,
      texto: `El enganche no tiene cargado el furgón de la 2da parte: no separé el ${e.bitren} en ${HOJA_SALIDAS}.`,
    };
  }
  if (!pelado(e.tractor2) && !pelado(e.chofer2)) {
    return {
      separar: false,
      texto: `El enganche no tiene cargado el tractor ni el chofer de la 2da parte: no separé el ${par(e)} en ${HOJA_SALIDAS}.`,
    };
  }
  return { separar: true };
}

const FLETERO = ["FLT", "FLETERO", "FLET", "TERCERO", "PP"];
const PATENTE = /^([A-Z]{3}\d{3}|[A-Z]{2}\d{3}[A-Z]{2})$/;

/* El tractor de la 2da como lo escribe él: el interno sin ceros adelante, o
   FLT si es un fletero (en Enganches un fletero va con la patente). */
export function tractorDePlanilla(v) {
  const t = txt(v).toUpperCase();
  if (/^\d{1,5}$/.test(t)) return t.replace(/^0+(?=\d)/, "");
  const p = pelado(t);
  return FLETERO.includes(p) || PATENTE.test(p) ? "FLT" : t;
}

const diaDe = (fecha) => (fecha ? Number(fecha.slice(8, 10)) : 0);
const diaDeLaCelda = (v) => Number.parseInt(txt(v), 10);

/* Separa el bitren `e` (de `datosParaSeparar`) en la hoja de salidas.
 * `conocidos` es `{tractor: chofer}` como él los escribe
 * (`choferesDeLaPlanilla`). Devuelve `{estado, hoja, ...}`; la hoja es la
 * misma que entró salvo en «separado»:
 *   · "separado"  `fila` (la de arriba, desde 0), `escritas` (los títulos de
 *                 lo que se escribió abajo), `distintos` ([{ref, titulo,
 *                 valor}]: lo que abajo ya tenía otra cosa y quedó) y
 *                 `quien` (el tractor y el chofer de la 2da, como van);
 *   · "ya"        sin combinadas en esos renglones y nada para escribir;
 *   · "cruce"     una combinada suya se cruza: `con` (como «H3:I4»);
 *   · "no_esta"   el par no está ese día (o no está el encabezado). */
export function separarBitren(hoja, e, conocidos = {}) {
  const enc = encabezadoSalidas(hoja, { conFC: true });
  if (!enc || !e) return { estado: "no_esta", hoja };
  const { fila, cols, ultima } = enc;
  const celdas = hoja.celdas || [];
  const valor = (r, c) => (c === undefined ? "" : (celdas[r] || [])[c]);
  const dia = diaDe(e.fecha);
  const delPar = [];
  let fcArriba = "";
  for (let r = fila + 1; r < celdas.length - 1; r += 1) {
    if (txt(valor(r, cols.fc))) fcArriba = valor(r, cols.fc);
    const esElPar = pelado(valor(r, cols.fur)) === pelado(e.bitren)
      && pelado(valor(r + 1, cols.fur)) === pelado(e.furgon2);
    if (esElPar && (!dia || diaDeLaCelda(fcArriba) === dia)) delPar.push(r);
  }
  if (!delPar.length) return { estado: "no_esta", hoja };
  const r = delPar[delPar.length - 1];

  // Las combinadas de esos dos renglones, hasta la última columna con título.
  const deEsos = normalizarCombinadas(hoja.combinadas).map(rangoCombinada)
    .filter((m) => m.r0 <= r + 1 && m.r1 >= r && m.c0 <= ultima);
  const esDeDos = (m) => m.r0 === r && m.r1 === r + 1 && m.c0 === m.c1 && m.c0 !== cols.fur;
  const con = deEsos.filter((m) => !esDeDos(m)).map(textoCombinada);
  if (con.length) return { estado: "cruce", hoja, fila: r, con };
  const sacar = new Set(deEsos.map(textoCombinada));

  // Lo que va abajo, columna por columna.
  const ub = encabezadoUbicacionFija(hoja);
  const tractor2 = tractorDePlanilla(e.tractor2);
  const chofer2 = choferLargo(e.chofer2, tractor2, conocidos);
  const abajo = [
    [cols.fc, valor(r, cols.fc)],
    [cols.fs, valor(r, cols.fs)],
    [cols.trac, tractor2],
    [cols.chofer, chofer2],
    [cols.cliente, txt(valor(r, cols.cliente)) ? valor(r, cols.cliente) : clienteCorto(e.cliente)],
    [ub && ub.fila === fila ? ub.ubic : undefined, EN_GALLEGOS],
  ].filter(([c, v]) => c !== undefined && c !== cols.fur && txt(v));
  const titulos = celdas[fila] || [];
  const renglon = [...(celdas[r + 1] || [])];
  const formato = { ...(hoja.formato || {}) };
  const escritas = [];
  const distintos = [];
  for (const [c, v] of abajo) {
    const ref = refDe(r + 1, c);
    const hay = renglon[c];
    if (txt(hay)) {
      if (!igual(hay, v)) distintos.push({ ref, titulo: txt(titulos[c]), valor: txt(hay) });
      continue;
    }
    while (renglon.length <= c) renglon.push("");
    renglon[c] = v;
    escritas.push(txt(titulos[c]));
    // Con la letra de arriba (el centrado incluido), sin su nota.
    if (!formato[ref]) {
      const { nota, ...deArriba } = formato[refDe(r, c)] || {};
      if (Object.keys(deArriba).length) formato[ref] = deArriba;
    }
  }
  if (!sacar.size && !escritas.length) return { estado: "ya", hoja, fila: r, distintos };
  const nueva = {
    ...hoja,
    celdas: celdas.map((f, i) => (i === r + 1 ? renglon : f)),
    formato,
    ...(hoja.combinadas ? { combinadas: normalizarCombinadas(hoja.combinadas).filter((t) => !sacar.has(t)) } : {}),
  };
  return { estado: "separado", hoja: nueva, fila: r, escritas, distintos, quien: [tractor2, chofer2].filter(Boolean).join(" · ") };
}

const yaTenia = (distintos) => (distintos?.length
  ? ` Abajo no cambié lo que ya estaba escrito: ${enumerar(distintos.map((x) => `${x.titulo} ${x.valor}`))}.`
  : "");

/* Lo que se le dice, en una línea, después de `separarBitren`. */
export function textoSeparar(r, e, planilla) {
  switch (r.estado) {
    case "separado":
      return `${planilla}: en ${HOJA_SALIDAS} el ${par(e)} quedó separado, con ${r.quien} en el renglón `
        + `del ${e.furgon2}. Antes quedó una copia en el Historial.${yaTenia(r.distintos)}`;
    case "ya":
      return `En ${HOJA_SALIDAS} el ${par(e)} ya estaba separado.${yaTenia(r.distintos)}`;
    case "cruce": {
      const una = r.con.length === 1;
      return `No separé el ${par(e)} en ${HOJA_SALIDAS}: se cruza con ${enumerar(r.con)}, que ya `
        + `${una ? "está combinada" : "están combinadas"} de otra forma. Arreglala y volvé a generar el correo.`;
    }
    default: {
      const dia = diaDe(e.fecha);
      return `No encontré el ${par(e)}${dia ? ` con F/C ${dia}` : ""} en ${HOJA_SALIDAS} para separarlo: no toqué nada.`;
    }
  }
}

export const textoSinPlanillaSalidas = (e) =>
  `No encontré una planilla con ${HOJA_SALIDAS} para separar el ${par(e)}.`;
